/**
 * Boat Connect Authentication Service
 * =====================================
 * Gestisce l'autenticazione con il backend Cheshire Cat
 * utilizzando il plugin Boat Connect Auth.
 */

import React from 'react';

// ==================== TYPES ====================

export interface VerifySessionResponse {
  valid: boolean;
  user_id?: string;
  user_full_name?: string;
  user_email?: string;
  created_at?: string;
  expires_at?: string;
  client_ip?: string;
  status?: 'active' | 'expired' | 'revoked';
  error?: string;
}

export interface InitSessionRequest {
  user_id: string;
}

export interface InitSessionResponse {
  redirect_url: string;
  session_id: string;
  user_id: string;
  expires_at: string;
  created_at: string;
}

export interface ServiceStatus {
  service: string;
  status: string;
  version: string;
  active_sessions: number;
  frontend_url: string;
  auth_mode: string;
  rate_limiting: boolean;
  webhooks_enabled: boolean;
}

// ==================== CONFIGURATION ====================

const AUTH_SERVICE_BASE = 
  process.env.REACT_APP_AUTH_SERVICE_URL || 
  "http://localhost:1865";

const ENDPOINTS = {
  VERIFY: `${AUTH_SERVICE_BASE}/custom/boatconnect/auth/verify`,
  INIT: `${AUTH_SERVICE_BASE}/custom/boatconnect/auth/init`,
  STATUS: `${AUTH_SERVICE_BASE}/custom/boatconnect/status`,
  SESSIONS: `${AUTH_SERVICE_BASE}/custom/boatconnect/sessions`,
} as const;

// ==================== SERVICE ====================

export class BoatConnectAuthService {
  /**
   * Verifica una sessione esistente con il backend
   */
  static async verifySession(sessionId: string): Promise<VerifySessionResponse> {
    try {
      console.log("🔍 Verifying session with Boat Connect Auth...");
      console.log(`📡 Endpoint: ${ENDPOINTS.VERIFY}`);
      console.log(`🔑 Session ID: ${sessionId}`);
      
      const response = await fetch(`${ENDPOINTS.VERIFY}?session_id=${sessionId}`);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      }
      
      const data: VerifySessionResponse = await response.json();
      
      if (data.valid) {
        console.log("✅ Session verified successfully!");
        console.log(`👤 User ID: ${data.user_id}`);
        console.log(`📅 Created: ${data.created_at}`);
        console.log(`⏰ Expires: ${data.expires_at}`);
        console.log(`🌐 Client IP: ${data.client_ip || 'N/A'}`);
        console.log(`📊 Status: ${data.status || 'active'}`);
      } else {
        console.warn("⚠️ Invalid session:", data.error);
      }
      
      return data;
    } catch (error) {
      console.error("❌ Error verifying session:", error);
      return {
        valid: false,
        error: error instanceof Error ? error.message : "Network error"
      };
    }
  }

  /**
   * Inizializza una nuova sessione (chiamato dalla piattaforma principale)
   * Nota: Questo endpoint è tipicamente chiamato dal backend boatconnect.com
   */
  static async initSession(
    userId: string, 
    apiKey?: string
  ): Promise<InitSessionResponse | null> {
    try {
      console.log("🚀 Initializing new session...");
      console.log(`👤 User ID: ${userId}`);
      
      const headers: HeadersInit = {
        "Content-Type": "application/json",
      };
      
      // Se auth mode è API_KEY, aggiungi l'header
      if (apiKey) {
        headers["X-API-Key"] = apiKey;
      }
      
      const response = await fetch(ENDPOINTS.INIT, {
        method: "POST",
        headers,
        body: JSON.stringify({ user_id: userId }),
      });
      
      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(
          errorData.detail || `HTTP ${response.status}: ${response.statusText}`
        );
      }
      
      const data: InitSessionResponse = await response.json();
      
      console.log("✅ Session initialized!");
      console.log(`🔑 Session ID: ${data.session_id}`);
      console.log(`🔗 Redirect URL: ${data.redirect_url}`);
      
      return data;
    } catch (error) {
      console.error("❌ Error initializing session:", error);
      return null;
    }
  }

  /**
   * Ottiene lo status del servizio di autenticazione
   */
  static async getServiceStatus(): Promise<ServiceStatus | null> {
    try {
      console.log("📊 Fetching service status...");
      
      const response = await fetch(ENDPOINTS.STATUS);
      
      if (!response.ok) {
        throw new Error(`HTTP ${response.status}`);
      }
      
      const data: ServiceStatus = await response.json();
      
      console.log("✅ Service status:", data);
      
      return data;
    } catch (error) {
      console.error("❌ Error fetching service status:", error);
      return null;
    }
  }

  /**
   * Helper: Estrae session_id dall'URL
   */
  static getSessionIdFromUrl(): string | null {
    const params = new URLSearchParams(window.location.search);
    return params.get('session_id');
  }

  /**
   * Helper: Rimuove session_id dall'URL (dopo verifica)
   */
  static cleanSessionIdFromUrl(): void {
    const url = new URL(window.location.href);
    if (url.searchParams.has('session_id')) {
      url.searchParams.delete('session_id');
      window.history.replaceState({}, '', url.toString());
      console.log("🧹 Session ID removed from URL");
    }
  }

  /**
   * Helper: Verifica se un userId è un guest
   */
  static isGuestUser(userId: string): boolean {
    return !userId || userId === 'guest' || userId.startsWith('guest_');
  }

  /**
   * Helper: Verifica se un userId è autenticato
   */
  static isAuthenticatedUser(userId: string): boolean {
    return !this.isGuestUser(userId);
  }
}

// ==================== HOOKS ====================

/**
 * Hook React per verificare la sessione automaticamente
 * Usage: const { userId, isLoading, error } = useBoatConnectAuth();
 */
export const useBoatConnectAuth = () => {
  const [userId, setUserId] = React.useState<string | null>(null);
  const [isLoading, setIsLoading] = React.useState(true);
  const [error, setError] = React.useState<string | null>(null);
  const [sessionData, setSessionData] = React.useState<VerifySessionResponse | null>(null);

  React.useEffect(() => {
    const verifySession = async () => {
      const sessionId = BoatConnectAuthService.getSessionIdFromUrl();
      
      if (!sessionId) {
        setIsLoading(false);
        return;
      }

      try {
        const data = await BoatConnectAuthService.verifySession(sessionId);
        
        if (data.valid && data.user_id) {
          setUserId(data.user_id);
          setSessionData(data);
          setError(null);
          
          // Opzionale: rimuovi session_id dall'URL dopo verifica
          BoatConnectAuthService.cleanSessionIdFromUrl();
        } else {
          setError(data.error || "Invalid session");
        }
      } catch (err) {
        setError(err instanceof Error ? err.message : "Unknown error");
      } finally {
        setIsLoading(false);
      }
    };

    verifySession();
  }, []);

  return {
    userId,
    isLoading,
    error,
    sessionData,
    isGuest: userId ? BoatConnectAuthService.isGuestUser(userId) : true,
    isAuthenticated: userId ? BoatConnectAuthService.isAuthenticatedUser(userId) : false,
  };
};

// ==================== EXPORTS ====================

export default BoatConnectAuthService;
